import React, { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { api, inr, imageUrl, isProductInStock } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { useWishlist } from "@/context/WishlistContext";
import { useAuth } from "@/context/AuthContext";
import { useProductSeo } from "@/hooks/useProductSeo";
import { Button } from "@/components/ui/button";
import { ProductCard } from "@/components/ProductCard";
import { RichText } from "@/components/RichText";
import { Heart, Plus, Minus, ChevronLeft, ShieldCheck, Leaf, Sparkles, Play, ChevronDown, MapPin, IndianRupee, Gift } from "lucide-react";
import { toast } from "sonner";

export default function ProductDetailPage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedIdx, setSelectedIdx] = useState(0);
  const [selectedPack, setSelectedPack] = useState(null);   // multi-pack option index
  const [selectedVariant, setSelectedVariant] = useState(null); // variant index
  const [related, setRelated] = useState([]);
  const [whatsInIt, setWhatsInIt] = useState([]);
  const [showVideo, setShowVideo] = useState(false);

  const { getItem, addItem, setQty } = useCart();
  const { has, toggle } = useWishlist();
  const { user } = useAuth();

  useProductSeo(product);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        // slug endpoint; fallback to id
        let p;
        try {
          p = (await api.get(`/products/slug/${slug}`)).data;
        } catch {
          p = (await api.get(`/products/${slug}`)).data;
        }
        if (!isProductInStock(p)) {
          setProduct(null);
          return;
        }
        setProduct(p);
        setSelectedIdx(p.primary_image_index || 0);
        const includedItems = p.whats_in_it || [];
        if (p.whats_in_it_expanded) {
          setWhatsInIt(p.whats_in_it_expanded);
        } else if (includedItems.length) {
          try {
            const { data } = await api.post("/products/related", {
              ids: includedItems.map((item) => item.product_id),
            });
            const productsById = new Map(data.map((includedProduct) => [includedProduct.id, includedProduct]));
            setWhatsInIt(includedItems.map((item) => ({
              ...item,
              product: productsById.get(item.product_id),
            })));
          } catch (error) {
            console.error("included products load failed", error);
            setWhatsInIt(includedItems.map((item) => ({ ...item, product: null })));
          }
        } else {
          setWhatsInIt([]);
        }
        if (p.related_product_ids?.length) {
          const rel = await api.post("/products/related", { ids: p.related_product_ids });
          setRelated(rel.data.filter(isProductInStock));
        } else {
          const cat = await api.get("/products", { params: { category_id: p.category_id, limit: 8 } });
          setRelated(cat.data.filter((x) => x.id !== p.id && isProductInStock(x)).slice(0, 4));
        }
      } catch {
        setProduct(null);
      } finally {
        setLoading(false);
      }
    })();
  }, [slug]);

  const activePrice = useMemo(() => {
    if (selectedVariant != null && product?.variants?.[selectedVariant]) {
      const v = product.variants[selectedVariant];
      return { price: +v.price || product.price, mrp: +v.mrp || product.mrp, image: v.image };
    }
    if (selectedPack != null && product?.multi_packs?.[selectedPack]) {
      const p = product.multi_packs[selectedPack];
      return { price: +p.price || product.price, mrp: +p.mrp || product.mrp };
    }
    return { price: product?.price || 0, mrp: product?.mrp || 0 };
  }, [product, selectedPack, selectedVariant]);

  if (loading) return <div className="py-12 text-center text-hyd-text2">Loading product…</div>;
  if (!product) return (
    <div className="max-w-md mx-auto px-4 py-16 text-center">
      <p className="text-hyd-text2">Product not found.</p>
      <Link to="/" className="text-hyd-gold underline mt-2 inline-block">Back home</Link>
    </div>
  );

  const inCart = getItem("product", product.id);
  const qty = inCart?.qty || 0;
  const discount = activePrice.mrp > activePrice.price ? Math.round(((activePrice.mrp - activePrice.price) / activePrice.mrp) * 100) : 0;
  const wished = has(product.id);
  const currentImg = imageUrl(activePrice.image || product.images?.[selectedIdx] || product.images?.[0] || "");

  const handleAdd = () => {
    const variantSuffix = selectedVariant != null ? ` — ${product.variants[selectedVariant].name}` : "";
    const packSuffix = selectedPack != null ? ` — ${product.multi_packs[selectedPack].label}` : "";
    addItem({
      kind: "product",
      ref_id: product.id,
      name: product.name + variantSuffix + packSuffix,
      unit_price: activePrice.price,
      mrp: activePrice.mrp,
      image_url: currentImg,
      stock: product.stock,
      category_id: product.category_id,
      is_green_cracker: product.is_green_cracker,
    });
    toast.success(`${product.name} added to cart`);
  };

  const handleWish = async () => {
    if (!user) { toast.info("Please sign in to save items"); navigate("/login"); return; }
    const res = await toggle(product.id);
    if (res?.in_wishlist) toast.success("Added to wishlist");
    else toast("Removed from wishlist");
  };

  return (
    <div className="max-w-6xl mx-auto px-4 py-4">
      <button onClick={() => navigate(-1)} className="text-xs uppercase text-hyd-text2 hover:text-hyd-gold inline-flex items-center gap-1">
        <ChevronLeft className="w-3 h-3" /> Back
      </button>

      <div className="mt-3 grid md:grid-cols-2 gap-6">
        {/* Media */}
        <div className="space-y-3">
          <div className="relative aspect-square rounded-3xl overflow-hidden bg-hyd-panel border border-hyd-border">
            {showVideo && product.video_url ? (
              <video src={imageUrl(product.video_url)} controls autoPlay muted className="w-full h-full object-cover" />
            ) : currentImg ? (
              <img src={currentImg} alt={product.image_alt_text || product.name} className="w-full h-full object-contain" />
            ) : (
              <div className="w-full h-full grid place-items-center text-hyd-text3"><Sparkles className="w-16 h-16" /></div>
            )}
            {product.video_url && !showVideo && (
              <button
                onClick={() => setShowVideo(true)}
                className="absolute inset-0 grid place-items-center bg-hyd-bg/40 hover:bg-hyd-bg/20 transition-colors"
                aria-label="Play video"
                data-testid="product-play-video"
              >
                <div className="w-16 h-16 grid place-items-center rounded-full bg-hyd-gold shadow-glow-gold">
                  <Play className="w-7 h-7 text-hyd-bg fill-hyd-bg" />
                </div>
              </button>
            )}
            {discount > 0 && (
              <div className="absolute top-3 left-3 bg-hyd-red text-white text-xs font-bold px-3 py-1 rounded-full uppercase tracking-widest">
                {discount}% OFF
              </div>
            )}
          </div>

          {product.images?.length > 1 && (
            <div className="flex gap-2 overflow-x-auto pb-1">
              {product.images.map((img, i) => (
                <button
                  key={i}
                  onClick={() => { setSelectedIdx(i); setShowVideo(false); }}
                  className={`shrink-0 w-16 h-16 rounded-2xl overflow-hidden border-2 transition-colors ${selectedIdx === i && !showVideo ? "border-hyd-gold" : "border-hyd-border hover:border-hyd-gold/40"}`}
                >
                  <img src={imageUrl(img)} alt="" className="w-full h-full object-cover" />
                </button>
              ))}
              {product.video_url && (
                <button
                  onClick={() => setShowVideo(true)}
                  className={`shrink-0 w-16 h-16 rounded-2xl border-2 grid place-items-center transition-colors ${showVideo ? "border-hyd-gold text-hyd-gold" : "border-hyd-border text-hyd-text2 hover:border-hyd-gold/40"}`}
                >
                  <Play className="w-5 h-5" />
                </button>
              )}
            </div>
          )}
        </div>

        {/* Info */}
        <div>
          <div className="flex items-center gap-1.5 flex-wrap mb-2">
            {product.is_bestseller && <Tag color="red">Bestseller</Tag>}
            {product.is_new && <Tag color="gold">New</Tag>}
            {product.is_green_cracker && <Tag color="emerald"><Leaf className="w-2.5 h-2.5 mr-0.5" />Green</Tag>}
          </div>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold">{product.name}</h1>
          {product.brand && <div className="text-hyd-text3 text-xs uppercase tracking-widest mt-1">{product.brand}</div>}
          {product.short_description && (
            <RichText html={product.short_description} className="text-hyd-text2 mt-3 text-sm" />
          )}

          {/* Price */}
          <div className="mt-4 flex items-baseline gap-3">
            <div className="text-hyd-gold font-display text-3xl font-bold">{inr(activePrice.price)}</div>
            {activePrice.mrp > activePrice.price && <div className="text-hyd-text3 line-through">{inr(activePrice.mrp)}</div>}
            {discount > 0 && <div className="text-emerald-400 text-sm font-bold">Save {inr(activePrice.mrp - activePrice.price)}</div>}
          </div>
          {/* Multi-pack chooser */}
          {product.multi_packs?.length > 0 && (
            <div className="mt-5">
              <div className="text-xs uppercase tracking-widest text-hyd-text2 mb-2">Choose a pack</div>
              <div className="grid grid-cols-3 gap-2">
                <PackTile active={selectedPack == null} onClick={() => setSelectedPack(null)} label="Single" price={product.price} mrp={product.mrp} />
                {product.multi_packs.map((mp, i) => (
                  <PackTile
                    key={i}
                    active={selectedPack === i}
                    onClick={() => setSelectedPack(i)}
                    label={mp.label || `Pack of ${mp.qty}`}
                    price={+mp.price}
                    mrp={+mp.mrp}
                  />
                ))}
              </div>
            </div>
          )}

          {/* Variants */}
          {product.variants?.length > 0 && (
            <div className="mt-5">
              <div className="text-xs uppercase tracking-widest text-hyd-text2 mb-2">Variants</div>
              <div className="flex flex-wrap gap-2">
                {product.variants.map((v, i) => (
                  <button
                    key={i}
                    onClick={() => setSelectedVariant(selectedVariant === i ? null : i)}
                    className={`px-3 py-1.5 rounded-full border text-xs ${selectedVariant === i ? "bg-hyd-gold text-hyd-bg border-hyd-gold" : "border-hyd-border text-hyd-text2 hover:border-hyd-gold"}`}
                  >
                    {v.name}{v.price ? ` · ${inr(v.price)}` : ""}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Quantity + Add */}
          <div className="mt-6 flex items-center gap-3">
            {qty > 0 ? (
              <div className="bg-hyd-panel border border-hyd-red rounded-full flex items-center px-2 py-1.5 gap-2">
                <button onClick={() => setQty("product", product.id, qty - 1, product.stock)} className="w-8 h-8 grid place-items-center text-hyd-red rounded-full hover:bg-hyd-bg"><Minus className="w-4 h-4" /></button>
                <span className="min-w-[24px] text-center font-bold text-hyd-text">{qty}</span>
                <button onClick={() => setQty("product", product.id, qty + 1, product.stock)} disabled={qty >= Number(product.stock)} className="w-8 h-8 grid place-items-center text-hyd-gold rounded-full hover:bg-hyd-bg disabled:opacity-40 disabled:cursor-not-allowed"><Plus className="w-4 h-4" /></button>
              </div>
            ) : (
              <Button data-testid="product-detail-add" onClick={handleAdd} className="bg-hyd-red hover:bg-hyd-redDim text-white font-bold uppercase tracking-wider h-11 rounded-full px-6">
                + Add to cart
              </Button>
            )}
            <button
              onClick={handleWish}
              className="w-11 h-11 rounded-full border border-hyd-border grid place-items-center hover:border-hyd-red transition-colors"
              aria-label={wished ? "Remove from wishlist" : "Add to wishlist"}
              data-testid="product-detail-wish"
            >
              <Heart className={`w-4 h-4 ${wished ? "fill-hyd-red text-hyd-red" : "text-hyd-text2"}`} />
            </button>
          </div>

          {(product.product_type || product.no_of_pieces) && (
            <div className="mt-3 flex flex-wrap gap-2" data-testid="product-package-tags">
              {product.product_type && <Tag>{product.product_type === "packet" ? "1 Packet" : "1 Box"}</Tag>}
              {product.no_of_pieces && <Tag>{product.no_of_pieces} pieces</Tag>}
            </div>
          )}

          {/* Compliance ribbon */}
          <div className="mt-6 grid grid-cols-2 gap-2 text-[11px]">
            <div className="flex items-center gap-2 bg-hyd-panel border border-hyd-border rounded-2xl px-3 py-2 text-hyd-text2">
              <MapPin className="w-4 h-4 text-hyd-gold" />
              <span>Delivery Only in Hyderabad</span>
            </div>
            <div className="flex items-center gap-2 bg-hyd-panel border border-hyd-border rounded-2xl px-3 py-2 text-hyd-text2">
              <IndianRupee className="w-4 h-4 text-hyd-gold" />
              <span>Min order value ₹2300</span>
            </div>
            <div className="flex items-center gap-2 bg-hyd-panel border border-hyd-border rounded-2xl px-3 py-2 text-hyd-text2">
              <ShieldCheck className="w-4 h-4 text-hyd-gold" />
              <span>Genuine Standard Crackers Only</span>
            </div>
          </div>

          {/* SEO tags / keywords — browse-friendly chip rail */}
          {(product.seo_tags?.length > 0) && (
            <div className="mt-5 flex flex-wrap gap-1.5">
              {product.seo_tags.map((t, i) => (
                <span key={i} className="text-[10px] uppercase font-bold tracking-widest px-2 py-0.5 rounded-full bg-hyd-panel border border-hyd-border text-hyd-text2">
                  #{t}
                </span>
              ))}
            </div>
          )}

          {/* Description */}
          {product.description && (
            <div className="mt-6 bg-hyd-panel border border-hyd-border rounded-2xl p-4">
              <div className="text-xs uppercase tracking-widest text-hyd-gold mb-2">Full description</div>
              <RichText html={product.description} className="text-hyd-text2 text-sm leading-relaxed" />
            </div>
          )}
        </div>
      </div>

      {/* SEO FAQ — rendered visibly AND via JSON-LD schema inside useProductSeo */}
      {whatsInIt.length > 0 && (
        <section className="mt-10">
          <h2 className="font-display text-2xl font-bold mb-4 flex items-center gap-2">
            <Gift className="w-5 h-5 text-hyd-gold" /> What&apos;s in it
            <span className="text-hyd-text2 text-sm font-normal">({whatsInIt.length} products)</span>
          </h2>
          <div className="grid gap-3">
            {whatsInIt.map((item) => {
              const includedProduct = item.product;
              if (!includedProduct) {
                return (
                  <div key={item.product_id} className="bg-hyd-panel border border-hyd-border rounded-2xl p-3 text-hyd-text3 text-sm">
                    Product unavailable · qty {item.qty}
                  </div>
                );
              }
              const includedImage = imageUrl(
                includedProduct.images?.[includedProduct.primary_image_index || 0] ||
                includedProduct.images?.[0] || "",
              );
              return (
                <div
                  key={item.product_id}
                  data-testid={`product-included-item-${item.product_id}`}
                  className="bg-hyd-panel border border-hyd-border rounded-2xl p-3 flex gap-3 items-center"
                >
                  <div className="w-16 h-16 rounded-xl overflow-hidden bg-hyd-panel2 shrink-0 border border-hyd-border">
                    {includedImage && <img src={includedImage} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm line-clamp-1">{includedProduct.name}</div>
                    <div className="text-xs text-hyd-text2 mt-1">
                      <span className="bg-hyd-bg border border-hyd-border rounded-full px-2 py-0.5 text-hyd-gold font-bold">× {item.qty}</span>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      )}

      {/* SEO FAQ — rendered visibly AND via JSON-LD schema inside useProductSeo */}
      {product.seo_faq?.length > 0 && (
        <section className="mt-10 max-w-3xl">
          <h2 className="font-display text-2xl font-bold mb-3">Frequently asked questions</h2>
          <div className="space-y-2">
            {product.seo_faq.map((f, i) => (
              <FaqItem key={i} question={f.question} answer={f.answer} />
            ))}
          </div>
        </section>
      )}

      {/* Related */}
      {related.length > 0 && (
        <section className="mt-12">
          <h2 className="font-display text-2xl font-bold mb-4">You may also like</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {related.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}
    </div>
  );
}

function Tag({ children, color = "gold" }) {
  const cls =
    color === "gold" ? "bg-hyd-gold/15 text-hyd-gold"
    : color === "red" ? "bg-hyd-red/15 text-hyd-red"
    : "bg-emerald-500/15 text-emerald-300";
  return (
    <span className={`text-[10px] uppercase font-bold tracking-widest px-2 py-0.5 rounded-full inline-flex items-center ${cls}`}>
      {children}
    </span>
  );
}

function FaqItem({ question, answer }) {
  const [open, setOpen] = useState(false);
  return (
    <div className="bg-hyd-panel border border-hyd-border rounded-2xl overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
        data-testid="product-faq-toggle"
      >
        <span className="text-sm font-bold text-hyd-text">{question}</span>
        <ChevronDown className={`w-4 h-4 text-hyd-gold transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div className="px-4 pb-3 text-sm text-hyd-text2 whitespace-pre-wrap">{answer}</div>
      )}
    </div>
  );
}

function PackTile({ label, price, mrp, active, onClick }) {
  const off = mrp > price ? Math.round(((mrp - price) / mrp) * 100) : 0;
  return (
    <button
      onClick={onClick}
      className={`relative text-left p-3 rounded-2xl border-2 transition-colors ${active ? "border-hyd-gold bg-hyd-gold/10" : "border-hyd-border bg-hyd-panel hover:border-hyd-gold/50"}`}
    >
      <div className={`text-xs font-bold uppercase tracking-widest ${active ? "text-hyd-gold" : "text-hyd-text2"}`}>{label}</div>
      <div className="text-hyd-text font-bold mt-1">{inr(price)}</div>
      {mrp > price && <div className="text-hyd-text3 text-[11px] line-through">{inr(mrp)}</div>}
      {off > 0 && <div className="absolute top-1 right-1 text-[9px] font-bold bg-hyd-red text-white px-1.5 py-0.5 rounded-full">{off}%</div>}
    </button>
  );
}
